import { FunctionComponent, useMemo } from "react";
import { MyLineTask } from "../../types/tasks";
import { Autocomplete, TextField, Typography, IconButton, Card, Divider } from '../../tailwindcomponents';
import { palette } from '../../theme/palette';
import { redirectToMxi } from './../../config/mxiProvider';
import { redirectToIdmrTask, redirectToIdmrWorkPackage } from "../../config/idmrProvider";
import { Search } from '../../components/icons';
import { isBoeingAircraft } from "../../utils/aircraftUtils";
import { formatIcelandicDateTime, parseDateTimeToTimestamp } from '../../utils/dateUtils';

type LineTaskProps = {
    myLineTasks?: MyLineTask[],
    loading: Boolean,
    allTails: Array<string>,
    tailFilter: string,
    onTailFilterChange: (tail: string) => void,
}

const getWorkpackageStartTime = (task: MyLineTask) => {
    const taskWithAlternativeKeys = task as MyLineTask & {
        woStartTime?: string;
        wo_start_date?: string;
        woStartDate?: string;
    };

    return task.wo_start_time
        ?? taskWithAlternativeKeys.woStartTime
        ?? taskWithAlternativeKeys.wo_start_date
        ?? taskWithAlternativeKeys.woStartDate;
};


const LineTasks: FunctionComponent<LineTaskProps> = ({ myLineTasks, loading, allTails, tailFilter, onTailFilterChange }) => {
    const filteredTasks = useMemo(() => {
        if (!tailFilter) return myLineTasks;
        return myLineTasks?.filter(task => task.tail_number === tailFilter);
    }, [myLineTasks, tailFilter]);

    const groupedTasks = useMemo(() => {
        const groups: Array<{ woBarcode: string; woName: string; tailNumber: string; woStartTime?: string; earliestTaskTimestamp: number; tasks: Array<MyLineTask> }> = [];
        const groupByBarcode = new Map<string, { woBarcode: string; woName: string; tailNumber: string; woStartTime?: string; earliestTaskTimestamp: number; tasks: Array<MyLineTask> }>();

        (filteredTasks ?? []).forEach((task) => {
            const taskTimestamp = parseDateTimeToTimestamp(task.mxi_start_date_text);
            const existingGroup = groupByBarcode.get(task.wo_barcode);
            if (existingGroup) {
                existingGroup.tasks.push(task);
                existingGroup.earliestTaskTimestamp = Math.min(existingGroup.earliestTaskTimestamp, taskTimestamp);
                if (!existingGroup.woStartTime) {
                    existingGroup.woStartTime = getWorkpackageStartTime(task);
                }
                return;
            }

            const newGroup = {
                woBarcode: task.wo_barcode,
                woName: task.wo_name,
                tailNumber: task.tail_number,
                woStartTime: getWorkpackageStartTime(task),
                earliestTaskTimestamp: taskTimestamp,
                tasks: [task],
            };
            groupByBarcode.set(task.wo_barcode, newGroup);
            groups.push(newGroup);
        });

        groups.forEach((group) => {
            group.tasks.sort((a, b) => parseDateTimeToTimestamp(a.mxi_start_date_text) - parseDateTimeToTimestamp(b.mxi_start_date_text));
        });

        return groups.sort((a, b) => {
            const woStartA = parseDateTimeToTimestamp(a.woStartTime);
            const woStartB = parseDateTimeToTimestamp(b.woStartTime);
            if (woStartA !== woStartB) {
                return woStartA - woStartB;
            }

            return a.earliestTaskTimestamp - b.earliestTaskTimestamp;
        });
    }, [filteredTasks]);

    return (
        <div>
            {loading ?
                <Typography text={`Loading`} color={'primary'} variant={'h6'} align={'center'} margin={[0, 0, 1, 0]} />
                :
                (!loading && myLineTasks?.length !== undefined) &&
                    (myLineTasks?.length !== undefined && myLineTasks.length > 0) ?
                    <>
                        <div className="mb-4">
                            <Autocomplete
                                fullWidth
                                noOptionsText="No tails found"
                                options={allTails}
                                value={tailFilter}
                                clearOnEscape
                                autoHighlight
                                onChange={(e: any, newValue: string | null) => onTailFilterChange(newValue ?? "")}
                                renderInput={(params) => (
                                    <TextField {...params} label="Filter by Tail" variant="standard" />
                                )}
                                className="max-w-[200px]"
                            />
                        </div>
                        {filteredTasks && filteredTasks.length > 0 ?
                            <>
                                {groupedTasks.map((group, groupIndex) => (
                                    <div key={group.woBarcode} className="mb-4">
                                        <Typography
                                            text={`${group.woName} (${group.tasks.length}) - ${group.tailNumber} - ${formatIcelandicDateTime(group.woStartTime)}`}
                                            variant='h6'
                                            margin={[0, 0, 1, 0]}
                                            fontWeight='bold'
                                        />
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            {group.tasks.map((task) => {
                                                // Paper (which Card wraps) sets `color` unconditionally via inline
                                                // style, so a `text-black/[0.87]` className can never win — the
                                                // PAUSE branch's text color must be passed via `style` instead.
                                                const cardBorderClass = task.status === "IN WORK" ? "border-0 border-solid border-r-[13px] border-r-accent-blue cursor-grab" : task.status === "COMPLETE" ? "border-0 border-solid border-r-[13px] border-r-status-complete cursor-grab" : task.status === "ACTV" ? "border-0 border-solid border-r-[13px] border-r-status-actv cursor-grab" : task.status === "PAUSE" ? "border-0 border-solid border-r-[13px] border-r-status-pause cursor-grab" : task.status === "COMMIT" ? "border-0 border-solid border-r-[13px] border-r-status-night cursor-grab" : "border-0 border-solid border-r-[13px] border-r-status-cancel cursor-grab";
                                                return (
                                                <div key={task.task_barcode}>
                                                    <Card className={cardBorderClass} style={task.status === "PAUSE" ? { color: 'rgba(0,0,0,0.87)' } : undefined}>
                                                        <div className="p-3">
                                                            {/* Header: Tail + WO Name + Start date (highlighted) */}
                                                            <div className="flex items-start justify-between mb-1">
                                                                <div className="flex flex-col gap-0.5">
                                                                    <span className="text-[1.4rem] font-[800] text-accent-blue tracking-[0.5px]">{task.tail_number}</span>
                                                                    <span className="text-base font-bold text-content">Wo name: {task.wo_name}</span>
                                                                    <span className="text-base font-bold text-[#1565c0]">Start: {task.mxi_start_date_text}</span>
                                                                </div>
                                                                <div className="flex items-center gap-1">
                                                                    <IconButton
                                                                        className="text-xs font-semibold py-0.5 px-2 rounded border border-solid border-[color:var(--ds-border)] hover:bg-black/[0.04]"
                                                                        style={{ color: palette.accentBlue }}
                                                                        onClick={() => redirectToMxi(task.wo_barcode)}
                                                                    >
                                                                        WO MXi
                                                                    </IconButton>
                                                                    {(task.status !== "COMPLETE" && task.is_unassigned === false && !isBoeingAircraft(task.inventory)) &&
                                                                        <IconButton
                                                                            className="text-xs font-semibold py-0.5 px-2 rounded border border-solid border-[color:var(--ds-border)] hover:bg-black/[0.04]"
                                                                            style={{ color: palette.accentBlue }}
                                                                            onClick={() => redirectToIdmrWorkPackage(task.wo_barcode)}
                                                                        >
                                                                            WO eSign
                                                                        </IconButton>
                                                                    }
                                                                    <IconButton
                                                                        aria-label="search"
                                                                        className="p-1"
                                                                    >
                                                                        <Search color="primary" fontSize="small" />
                                                                    </IconButton>
                                                                </div>
                                                            </div>

                                                            <Divider className="my-1.5 bg-[color:var(--ds-border)]" />

                                                            {/* Secondary info grid */}
                                                            <div className="grid grid-cols-2 gap-y-1 gap-x-4 mb-1.5">
                                                                <div className="flex flex-col">
                                                                    <span className="text-content text-xs font-medium uppercase tracking-[0.5px] mb-0.5">Task</span>
                                                                    <span className="text-xs font-medium text-content">{task.task_name}</span>
                                                                </div>
                                                                <div className="flex flex-col">
                                                                    <span className="text-content text-xs font-medium uppercase tracking-[0.5px] mb-0.5">Status</span>
                                                                    <span className="inline-block text-xs font-bold py-px px-2 rounded bg-black/[0.08]">{task.status}</span>
                                                                </div>
                                                            </div>

                                                            {/* Assigned Labour */}
                                                            <div className="mt-1 p-1.5 bg-black/[0.04] rounded">
                                                                <span className="text-xs font-semibold uppercase tracking-[0.5px] text-content mb-0.5">Assigned Labour</span>
                                                                <div className="text-[0.8rem] font-medium text-accent-blue">{task.assigned_employees}</div>
                                                            </div>

                                                            <Divider className="my-1.5 bg-[color:var(--ds-border)]" />

                                                            {/* Actions */}
                                                            <div className="flex gap-1 mt-1">
                                                                <IconButton
                                                                    className="text-xs font-semibold py-0.5 px-2 rounded border border-solid border-[color:var(--ds-border)] hover:bg-black/[0.04]"
                                                                    style={{ color: palette.accentBlue }}
                                                                    onClick={() => redirectToMxi(task.task_barcode)}
                                                                >
                                                                    MXI
                                                                </IconButton>
                                                                {(task.status !== "COMPLETE" && task.is_unassigned === false && !isBoeingAircraft(task.inventory)) &&
                                                                    <IconButton
                                                                        className="text-xs font-semibold py-0.5 px-2 rounded border border-solid border-[color:var(--ds-border)] hover:bg-black/[0.04]"
                                                                        style={{ color: palette.accentBlue }}
                                                                        onClick={() => redirectToIdmrTask(task.wo_barcode, task.task_barcode)}
                                                                    >
                                                                        eSign
                                                                    </IconButton>
                                                                }
                                                            </div>
                                                        </div>
                                                    </Card>
                                                </div>
                                                );
                                            })}
                                        </div>
                                        {groupIndex < groupedTasks.length - 1 && (
                                            <Divider className="mt-4 border-b-2 border-[color:var(--ds-border-strong)]" />
                                        )}
                                    </div>
                                ))}
                            </>
                            :
                            <Typography text={`No tasks match the selected tail`} color={'primary'} variant={'h6'} align={'center'} margin={[0, 0, 1, 0]} />
                        }
                    </>
                    :
                    <Typography text={`No tasks`} color={'primary'} variant={'h6'} align={'center'} margin={[0, 0, 1, 0]} />
            }
        </div >
    )
}

export default LineTasks;